import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { AppErrorFallback } from '@/components/ui';

/**
 * Wired as `defaultErrorComponent` on `createRouter` in `App.tsx` — every
 * route inherits this fallback unless it declares its own `errorComponent`.
 *
 * Captures the error to Sentry once per mount (the effect dep-array gates on
 * `error`, so a fresh throw replaces the captured event rather than appending
 * a duplicate on re-render). Tagged `surface: 'route-error'` so the Sentry
 * search filter mirrors the per-site `surface` convention used elsewhere in
 * the web (see `lib/sentry.ts` capture-site comment).
 *
 * Reason this exists: TanStack Router catches `validateSearch` / loader /
 * render throws INSIDE the router, so they never propagate to the root
 * `<Sentry.ErrorBoundary>` in `App.tsx`. Without this component, every
 * router-internal error renders the unbranded default fallback with NO
 * Sentry event captured — exactly the silence that hid the 2026-05-26
 * `/auth/callback` schema-strict failure.
 */
export function RouteErrorComponent({ error, reset }: ErrorComponentProps) {
  useEffect(() => {
    Sentry.captureException(error, {
      tags: { surface: 'route-error' },
    });
  }, [error]);

  return <AppErrorFallback onReset={reset} />;
}
